import axios, { AxiosResponse, AxiosError } from 'axios'
import { APIRequest, APIResponse } from '../stores/apiStore'

export class APIService {
  private static instance: APIService
  private requestId = 0

  static getInstance(): APIService {
    if (!APIService.instance) {
      APIService.instance = new APIService()
    }
    return APIService.instance
  }

  private generateRequestId(): string {
    return `req_${Date.now()}_${++this.requestId}`
  }

  private calculateResponseSize(data: any): number {
    try {
      return new Blob([JSON.stringify(data)]).size
    } catch {
      return 0
    }
  }

  async makeRequest(request: Partial<APIRequest>): Promise<APIResponse> {
    const startTime = Date.now()
    const requestId = this.generateRequestId()

    try {
      // Always attempt real API calls - no special handling or fallbacks

      // Build URL
      const url = new URL(request.endpoint?.path || '', request.baseUrl)
      
      // Add query parameters for GET requests
      if (request.queryParams && Object.keys(request.queryParams).length > 0) {
        Object.entries(request.queryParams).forEach(([key, value]) => {
          if (value && value.trim()) {
            url.searchParams.append(key, value)
          }
        })
      }

      // Configure axios request
      const config: any = {
        method: request.endpoint?.method || 'GET',
        url: url.toString(),
        headers: {
          ...request.headers,
          'X-Request-ID': requestId,
        },
        timeout: 60000, // 60 seconds timeout for real API calls
        withCredentials: false, // Don't send cookies with cross-origin requests
      }

      // Add request body for POST/PUT requests
      if (request.requestBody && ['POST', 'PUT'].includes(config.method)) {
        config.data = request.requestBody
      }

      // Make the request
      const response: AxiosResponse = await axios(config)
      const endTime = Date.now()
      const responseTime = endTime - startTime

      // Create response object
      const apiResponse: APIResponse = {
        id: this.generateRequestId(),
        requestId,
        endpoint: request.endpoint!,
        status: response.status,
        statusText: response.statusText,
        headers: response.headers as Record<string, string>,
        data: response.data,
        responseTime,
        size: this.calculateResponseSize(response.data),
        timestamp: endTime
      }

      return apiResponse
    } catch (error) {
      const endTime = Date.now()
      const responseTime = endTime - startTime

      if (error instanceof AxiosError) {
        // Provide detailed error information
        let errorMessage = error.message
        let errorData = null

        if (error.code === 'ERR_NETWORK') {
          errorMessage = 'Network Error: Unable to connect to the API. This could be due to CORS restrictions, network connectivity issues, or the API being unavailable.'
        } else if (error.code === 'ECONNABORTED') {
          errorMessage = 'Request Timeout: The API request took too long to respond.'
        } else if (error.response) {
          errorMessage = `HTTP ${error.response.status}: ${error.response.statusText}`
          errorData = error.response.data
        }

        const apiResponse: APIResponse = {
          id: this.generateRequestId(),
          requestId,
          endpoint: request.endpoint!,
          status: error.response?.status || 0,
          statusText: error.response?.statusText || 'Network Error',
          headers: (error.response?.headers as Record<string, string>) || {},
          data: errorData || {
            error: errorMessage,
            code: error.code,
            details: error.response?.data || null,
            timestamp: new Date().toISOString(),
            request_url: error.config?.url,
            request_method: error.config?.method?.toUpperCase()
          },
          responseTime,
          size: this.calculateResponseSize(errorData || { error: errorMessage }),
          timestamp: endTime,
          error: errorMessage
        }

        return apiResponse
      }

      // Handle non-Axios errors
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred'
      const apiResponse: APIResponse = {
        id: this.generateRequestId(),
        requestId,
        endpoint: request.endpoint!,
        status: 0,
        statusText: 'Unknown Error',
        headers: {},
        data: {
          error: errorMessage,
          timestamp: new Date().toISOString(),
          type: 'Unknown Error'
        },
        responseTime,
        size: this.calculateResponseSize({ error: errorMessage }),
        timestamp: endTime,
        error: errorMessage
      }

      return apiResponse
    }
  }

  generateCurl(request: Partial<APIRequest>, options: { 
    verbose?: boolean
    includeHeaders?: boolean
    pretty?: boolean
  } = {}): string {
    try {
      const { verbose = false, includeHeaders = true, pretty = false } = options
      
      // Validate required fields
      if (!request.endpoint || !request.baseUrl) {
        return '# Please select an endpoint and configure the base URL'
      }
      
      let curl = 'curl'
      
      if (verbose) {
        curl += ' -v'
      }

      // Add method
      if (request.endpoint?.method && request.endpoint.method !== 'GET') {
        curl += ` -X ${request.endpoint.method}`
      }

      // Add headers
      if (includeHeaders && request.headers) {
        Object.entries(request.headers).forEach(([key, value]) => {
          if (key && value) {
            curl += ` -H "${key}: ${value}"`
          }
        })
      }

      // Add request body
      if (request.requestBody && ['POST', 'PUT'].includes(request.endpoint?.method || '')) {
        try {
          const bodyStr = JSON.stringify(request.requestBody, null, pretty ? 2 : 0)
          curl += ` -d '${bodyStr}'`
        } catch (e) {
          curl += ` -d '${String(request.requestBody)}'`
        }
      }

      // Build URL with query parameters
      try {
        const url = new URL(request.endpoint.path || '', request.baseUrl)
        if (request.queryParams && Object.keys(request.queryParams).length > 0) {
          Object.entries(request.queryParams).forEach(([key, value]) => {
            if (key && value && String(value).trim()) {
              url.searchParams.append(key, String(value))
            }
          })
        }
        curl += ` "${url.toString()}"`
      } catch (e) {
        curl += ` "${request.baseUrl}${request.endpoint.path || ''}"`
      }

      return curl
    } catch (error) {
      console.error('Error generating cURL:', error)
      return '# Error generating cURL command. Please check your configuration.'
    }
  }

  generateCodeExample(request: Partial<APIRequest>, language: string): string {
    try {
      // Validate required fields
      if (!request.endpoint || !request.baseUrl) {
        return '// Please select an endpoint and configure the base URL'
      }

      // Build URL with query parameters
      let url: string
      try {
        const urlObj = new URL(request.endpoint.path || '', request.baseUrl)
        
        // Add query parameters
        if (request.queryParams && Object.keys(request.queryParams).length > 0) {
          Object.entries(request.queryParams).forEach(([key, value]) => {
            if (key && value && String(value).trim()) {
              urlObj.searchParams.append(key, String(value))
            }
          })
        }
        url = urlObj.toString()
      } catch (e) {
        url = `${request.baseUrl}${request.endpoint.path || ''}`
      }

      switch (language) {
        case 'javascript':
          return this.generateJavaScriptExample(request, url)
        case 'python':
          return this.generatePythonExample(request, url)
        case 'php':
          return this.generatePHPExample(request, url)
        case 'nodejs':
          return this.generateNodeJSExample(request, url)
        case 'flutter':
          return this.generateFlutterExample(request, url)
        case 'kotlin':
          return this.generateKotlinExample(request, url)
        case 'swift':
          return this.generateSwiftExample(request, url)
        case 'react-native':
          return this.generateReactNativeExample(request, url)
        default:
          return '// Unsupported language'
      }
    } catch (error) {
      console.error('Error generating code example:', error)
      return '// Error generating code example. Please check your configuration.'
    }
  }

  private generateJavaScriptExample(request: Partial<APIRequest>, url: string): string {
    const method = request.endpoint?.method || 'GET'
    const headers = JSON.stringify(request.headers || {}, null, 2)
    
    let code = `// JavaScript (Axios)
import axios from 'axios';

const config = {
  method: '${method.toLowerCase()}',
  url: '${url}',
  headers: ${headers}`

    if (request.requestBody && ['POST', 'PUT'].includes(method)) {
      code += `,
  data: ${JSON.stringify(request.requestBody, null, 2)}`
    }

    code += `
};

try {
  const response = await axios(config);
  console.log('Response:', response.data);
} catch (error) {
  console.error('Error:', error.response?.data || error.message);
}`

    return code
  }

  private generatePythonExample(request: Partial<APIRequest>, url: string): string {
    const method = request.endpoint?.method || 'GET'
    
    let code = `# Python (requests)
import requests
import json

url = "${url}"
headers = ${JSON.stringify(request.headers || {}, null, 2).replace(/"/g, "'")}

try:`

    if (request.requestBody && ['POST', 'PUT'].includes(method)) {
      code += `
    data = ${JSON.stringify(request.requestBody, null, 4).replace(/"/g, "'")}
    response = requests.${method.toLowerCase()}(url, headers=headers, json=data)`
    } else {
      code += `
    response = requests.${method.toLowerCase()}(url, headers=headers)`
    }

    code += `
    response.raise_for_status()
    print("Response:", response.json())
except requests.exceptions.RequestException as e:
    print("Error:", e)`

    return code
  }

  private generatePHPExample(request: Partial<APIRequest>, url: string): string {
    const method = request.endpoint?.method || 'GET'
    
    let code = `<?php
// PHP (cURL)
$url = "${url}";
$headers = array(`

    if (request.headers) {
      Object.entries(request.headers).forEach(([key, value]) => {
        code += `
    "${key}: ${value}",`
      })
    }

    code += `
);

$ch = curl_init();
curl_setopt($ch, CURLOPT_URL, $url);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);`

    if (request.requestBody && ['POST', 'PUT'].includes(method)) {
      code += `
curl_setopt($ch, CURLOPT_CUSTOMREQUEST, "${method}");
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode(${JSON.stringify(request.requestBody, null, 2)}));`
    }

    code += `

$response = curl_exec($ch);
$httpCode = curl_getinfo($ch, CURLINFO_HTTP_CODE);
curl_close($ch);

if ($response === false) {
    echo "cURL Error: " . curl_error($ch);
} else {
    echo "Response: " . $response;
}
?>`

    return code
  }

  private generateNodeJSExample(request: Partial<APIRequest>, url: string): string {
    const method = request.endpoint?.method || 'GET'
    
    let code = `// Node.js (Built-in modules)
const https = require('https');
const { URL } = require('url');

const url = new URL('${url}');
const options = {
  hostname: url.hostname,
  port: url.port || 443,
  path: url.pathname + url.search,
  method: '${method}',
  headers: ${JSON.stringify(request.headers || {}, null, 2)}
};

const req = https.request(options, (res) => {
  let data = '';
  
  res.on('data', (chunk) => {
    data += chunk;
  });
  
  res.on('end', () => {
    try {
      const response = JSON.parse(data);
      console.log('Response:', response);
    } catch (e) {
      console.log('Raw response:', data);
    }
  });
});

req.on('error', (error) => {
  console.error('Error:', error.message);
});`

    if (request.requestBody && ['POST', 'PUT'].includes(method)) {
      code += `

const postData = JSON.stringify(${JSON.stringify(request.requestBody, null, 2)});
req.write(postData);`
    }

    code += `

req.end();`

    return code
  }

  private generateFlutterExample(request: Partial<APIRequest>, url: string): string {
    const method = request.endpoint?.method || 'GET'
    
    let code = `// Flutter (Dart)
import 'package:http/http.dart' as http;
import 'dart:convert';

Future<void> makeRequest() async {
  try {
    final uri = Uri.parse('${url}');
    final headers = ${JSON.stringify(request.headers || {}, null, 4).replace(/"/g, "'")}
      .map((key, value) => MapEntry(key, value.toString()));`

    if (request.requestBody && ['POST', 'PUT'].includes(method)) {
      code += `
    
    final body = jsonEncode(${JSON.stringify(request.requestBody, null, 4).replace(/"/g, "'")});
    final response = await http.${method.toLowerCase()}(
      uri,
      headers: headers,
      body: body,
    );`
    } else {
      code += `
    
    final response = await http.${method.toLowerCase()}(
      uri,
      headers: headers,
    );`
    }

    code += `
    
    if (response.statusCode >= 200 && response.statusCode < 300) {
      final data = jsonDecode(response.body);
      print('Response: \$data');
    } else {
      print('Error: \${response.statusCode} - \${response.body}');
    }
  } catch (e) {
    print('Exception: \$e');
  }
}`

    return code
  }

  private generateKotlinExample(request: Partial<APIRequest>, url: string): string {
    const method = request.endpoint?.method || 'GET'
    
    let code = `// Kotlin (Android)
import okhttp3.*;
import okhttp3.MediaType.Companion.toMediaType
import okhttp3.RequestBody.Companion.toRequestBody
import java.io.IOException

class ApiClient {
    private val client = OkHttpClient()
    
    fun makeRequest() {
        val request = Request.Builder()
            .url("${url}")`

    if (request.headers) {
      Object.entries(request.headers).forEach(([key, value]) => {
        code += `
            .addHeader("${key}", "${value}")`
      })
    }

    if (request.requestBody && ['POST', 'PUT'].includes(method)) {
      code += `
            .${method.toLowerCase()}("""${JSON.stringify(request.requestBody, null, 12)}""".trimIndent()
                .toRequestBody("application/json".toMediaType()))`
    } else {
      code += `
            .${method.toLowerCase()}()`
    }

    code += `
            .build()
        
        client.newCall(request).enqueue(object : Callback {
            override fun onFailure(call: Call, e: IOException) {
                println("Request failed: \${e.message}")
            }
            
            override fun onResponse(call: Call, response: Response) {
                response.use {
                    if (response.isSuccessful) {
                        println("Response: \${response.body?.string()}")
                    } else {
                        println("Error: \${response.code} - \${response.body?.string()}")
                    }
                }
            }
        })
    }
}`

    return code
  }

  private generateSwiftExample(request: Partial<APIRequest>, url: string): string {
    const method = request.endpoint?.method || 'GET'
    
    let code = `// Swift (iOS)
import Foundation

func makeRequest() {
    guard let url = URL(string: "${url}") else {
        print("Invalid URL")
        return
    }
    
    var request = URLRequest(url: url)
    request.httpMethod = "${method}"`

    if (request.headers) {
      Object.entries(request.headers).forEach(([key, value]) => {
        code += `
    request.setValue("${value}", forHTTPHeaderField: "${key}")`
      })
    }

    if (request.requestBody && ['POST', 'PUT'].includes(method)) {
      code += `
    
    let jsonData = """
${JSON.stringify(request.requestBody, null, 4)}
    """.data(using: .utf8)
    request.httpBody = jsonData`
    }

    code += `
    
    let task = URLSession.shared.dataTask(with: request) { data, response, error in
        if let error = error {
            print("Error: \\(error.localizedDescription)")
            return
        }
        
        guard let httpResponse = response as? HTTPURLResponse else {
            print("Invalid response")
            return
        }
        
        if let data = data {
            do {
                let json = try JSONSerialization.jsonObject(with: data, options: [])
                print("Response: \\(json)")
            } catch {
                print("JSON parsing error: \\(error)")
                print("Raw response: \\(String(data: data, encoding: .utf8) ?? "")")
            }
        }
        
        print("Status code: \\(httpResponse.statusCode)")
    }
    
    task.resume()
}`

    return code
  }

  private generateReactNativeExample(request: Partial<APIRequest>, url: string): string {
    const method = request.endpoint?.method || 'GET'
    const headers = JSON.stringify(request.headers || {}, null, 2)
    
    let code = `// React Native
import React, { useEffect } from 'react';

const ApiExample = () => {
  const makeRequest = async () => {
    try {
      const config = {
        method: '${method}',
        headers: ${headers}`

    if (request.requestBody && ['POST', 'PUT'].includes(method)) {
      code += `,
        body: JSON.stringify(${JSON.stringify(request.requestBody, null, 6)})`
    }

    code += `
      };
      
      const response = await fetch('${url}', config);
      
      if (!response.ok) {
        throw new Error(\`HTTP error! status: \${response.status}\`);
      }
      
      const data = await response.json();
      console.log('Response:', data);
      
    } catch (error) {
      console.error('Error:', error.message);
    }
  };
  
  useEffect(() => {
    makeRequest();
  }, []);
  
  return null; // Your component JSX here
};

export default ApiExample;`

    return code
  }

  formatResponseTime(ms: number): string {
    if (ms < 1000) {
      return `${ms}ms`
    } else if (ms < 60000) {
      return `${(ms / 1000).toFixed(2)}s`
    } else {
      return `${(ms / 60000).toFixed(2)}m`
    }
  }

  formatResponseSize(bytes: number): string {
    if (bytes < 1024) {
      return `${bytes}B`
    } else if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(2)}KB`
    } else {
      return `${(bytes / (1024 * 1024)).toFixed(2)}MB`
    }
  }

  getStatusColor(status: number): string {
    if (status >= 200 && status < 300) {
      return 'text-green-600'
    } else if (status >= 300 && status < 400) {
      return 'text-yellow-600'
    } else if (status >= 400) {
      return 'text-red-600'
    }
    return 'text-gray-600'
  }

  generateAIPrompt(
    request: Partial<APIRequest>,
    response: APIResponse,
    language: string,
    style: 'structured' | 'conversational'
  ): string {
    try {
      if (!request.endpoint || !request.baseUrl) {
        return 'Error: Missing endpoint or base URL configuration'
      }

      // Build full URL with query parameters
      let fullUrl: string
      try {
        const urlObj = new URL(request.endpoint.path || '', request.baseUrl)
        if (request.queryParams && Object.keys(request.queryParams).length > 0) {
          Object.entries(request.queryParams).forEach(([key, value]) => {
            if (key && value && String(value).trim()) {
              urlObj.searchParams.append(key, String(value))
            }
          })
        }
        fullUrl = urlObj.toString()
      } catch (e) {
        fullUrl = `${request.baseUrl}${request.endpoint.path || ''}`
      }

      const method = request.endpoint.method || 'GET'
      const headers = request.headers || {}
      const queryParams = request.queryParams || {}
      const requestBody = request.requestBody

      // Get language display name
      const languageNames: Record<string, string> = {
        'javascript': 'JavaScript (Axios)',
        'python': 'Python (requests)',
        'php': 'PHP (cURL)',
        'nodejs': 'Node.js',
        'flutter': 'Flutter (Dart)',
        'kotlin': 'Kotlin (Android)',
        'swift': 'Swift (iOS)',
        'react-native': 'React Native'
      }
      const languageDisplay = languageNames[language] || language

      // Sample error responses based on API documentation
      const errorExamples = `// 400 Bad Request
{
  "error": "Bad Request",
  "message": "Invalid request parameters",
  "details": "placement is required"
}

// 401 Unauthorized
{
  "error": "Unauthorized",
  "message": "Invalid or missing API key"
}

// 403 Forbidden
{
  "error": "Forbidden",
  "message": "API key does not have required permissions"
}

// 404 Not Found
{
  "error": "Not Found",
  "message": "Endpoint not found"
}

// 500 Server Error
{
  "error": "Internal Server Error",
  "message": "An unexpected error occurred"
}`

      if (style === 'structured') {
        return this.generateStructuredPrompt(
          method,
          fullUrl,
          headers,
          queryParams,
          requestBody,
          response,
          languageDisplay,
          errorExamples
        )
      } else {
        return this.generateConversationalPrompt(
          method,
          fullUrl,
          headers,
          queryParams,
          requestBody,
          response,
          languageDisplay,
          errorExamples
        )
      }
    } catch (error) {
      console.error('Error generating AI prompt:', error)
      return 'Error generating AI prompt. Please check your configuration.'
    }
  }

  private generateStructuredPrompt(
    method: string,
    fullUrl: string,
    headers: Record<string, string>,
    queryParams: Record<string, string>,
    requestBody: any,
    response: APIResponse,
    languageDisplay: string,
    errorExamples: string
  ): string {
    let prompt = `TASK: Write code to integrate with the MomentPerks API

API DETAILS:
- Method: ${method.toUpperCase()}
- URL: ${fullUrl}`

    // Add headers section if there are non-default headers
    const relevantHeaders = Object.entries(headers).filter(([key]) => 
      !['X-Request-ID'].includes(key)
    )
    if (relevantHeaders.length > 0) {
      prompt += `\n- Headers:\n${relevantHeaders.map(([key, value]) => `  ${key}: ${value}`).join('\n')}`
    }

    // Add authentication details
    if (queryParams.api_key) {
      prompt += `\n\nAUTHENTICATION:\nAPI Key (api_key query parameter): ${queryParams.api_key}`
    }

    // Add request body for POST/PUT
    if (requestBody && ['POST', 'PUT'].includes(method.toUpperCase())) {
      prompt += `\n\nREQUEST BODY:\n${JSON.stringify(requestBody, null, 2)}`
    }

    // Add successful response example
    prompt += `\n\nSUCCESS RESPONSE (${response.status} ${response.statusText}):\n${JSON.stringify(response.data, null, 2)}`

    // Add error handling requirements
    prompt += `\n\nERROR HANDLING:\nHandle these HTTP status codes gracefully:
- 400: Bad Request - Invalid parameters
- 401: Unauthorized - Invalid API key  
- 403: Forbidden - Insufficient permissions
- 404: Not Found - Endpoint doesn't exist
- 500: Server Error - API server error

Error Response Examples:\n${errorExamples}`

    // Add implementation requirements
    prompt += `\n\nREQUIREMENTS:
1. Implement the API call in ${languageDisplay}
2. Include proper error handling for all status codes
3. Parse and handle the JSON response
4. Include retry logic for transient failures (optional)
5. Add timeout handling
6. Provide clear error messages to users

Please provide complete, production-ready code.`

    return prompt
  }

  private generateConversationalPrompt(
    method: string,
    fullUrl: string,
    headers: Record<string, string>,
    queryParams: Record<string, string>,
    requestBody: any,
    response: APIResponse,
    languageDisplay: string,
    errorExamples: string
  ): string {
    let prompt = `I need help integrating the MomentPerks API into my ${languageDisplay} app.

I'm making a ${method.toUpperCase()} request to:\n${fullUrl}`

    // Add authentication info
    if (queryParams.api_key) {
      prompt += `\n\nThe API uses an API key for authentication. The key \"${queryParams.api_key}\" is passed as a query parameter (api_key).`
    }

    // Add headers info if relevant
    const relevantHeaders = Object.entries(headers).filter(([key]) => 
      !['X-Request-ID'].includes(key)
    )
    if (relevantHeaders.length > 0) {
      prompt += `\n\nThe request includes these headers:\n${relevantHeaders.map(([key, value]) => `- ${key}: ${value}`).join('\n')}`
    }

    // Add request body info
    if (requestBody && ['POST', 'PUT'].includes(method.toUpperCase())) {
      prompt += `\n\nThe request body contains:\n${JSON.stringify(requestBody, null, 2)}`
    }

    // Add successful response info
    prompt += `\n\nWhen the request succeeds, I get a ${response.status} response that looks like this:\n${JSON.stringify(response.data, null, 2)}`

    // Add error handling request
    prompt += `\n\nHowever, I need to handle various error cases that might occur:
${errorExamples}

Can you write the complete ${languageDisplay} code to make this API call? I'd like it to:
1. Successfully make the request with all the parameters
2. Parse the JSON response when successful
3. Handle each type of error appropriately with clear error messages
4. Include timeout handling
5. Be production-ready code that I can use right away`

    return prompt
  }
}
